import { activityEntrySchema, paginated } from '@hearth/shared';
import { Activity as ActivityIcon } from 'lucide-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ActivityTable } from '@/components/activity-table';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { QueryState } from '@/components/query-state';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiRequest } from '@/lib/api';

const FILTERS = {
  all: 'All events',
  'auth:': 'Authentication',
  'user:': 'Account changes',
  'admin:': 'Admin actions',
} as const;
type Filter = keyof typeof FILTERS;

export const Route = createFileRoute('/_app/admin/activity')({
  validateSearch: (s: Record<string, unknown>): { page?: number; event?: Filter } => ({
    page: Number(s.page) > 1 ? Number(s.page) : undefined,
    event: typeof s.event === 'string' && s.event in FILTERS ? (s.event as Filter) : undefined,
  }),
  component: AdminActivity,
});

function AdminActivity() {
  const { page = 1, event = 'all' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const query = useQuery({
    queryKey: ['admin', 'activity', page, event],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ page: String(page), perPage: '50' });
      if (event !== 'all') params.set('event', event);
      return apiRequest(`/api/application/activity?${params}`, {
        schema: paginated(activityEntrySchema),
        signal,
      });
    },
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <PageHeader
        title="Activity"
        icon={ActivityIcon}
        description="Everything that happened on this panel: who, what, from where."
        actions={
          <Select
            value={event}
            onValueChange={(v) => void navigate({ search: { page: 1, event: v as Filter } })}
          >
            <SelectTrigger className="w-48" aria-label="Filter events">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(FILTERS).map(([k, label]) => (
                <SelectItem key={k} value={k}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <QueryState query={query}>
        {(data) => (
          <div className="space-y-4">
            <ActivityTable entries={data.data} />
            <Pagination
              {...data.meta}
              onPageChange={(p) => void navigate({ search: (s) => ({ ...s, page: p }) })}
            />
          </div>
        )}
      </QueryState>
    </>
  );
}
